// ============================================
// Stage Effects Module
// All stage-specific visual effects functions
// ============================================

import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config.js';
import {
    getDt,
    getBgPlanetY, setBgPlanetY,
    getDistantGiantY, setDistantGiantY,
    getSubspaceWarpOffset, setSubspaceWarpOffset
} from '../../store/index';
import {
    getBgStars,
    getBgBrightStars,
    getBgAsteroids,
    setLowOrbitPlanetMask
} from './background';

// ============================================
// starTwinkle() - 共通ユーティリティ関数
// 星の瞬き値を計算（0〜1基準の任意ベース/振幅/速度倍率）
// ============================================
import type { BgStar, BgAsteroid, StageConfig } from '../../types/game-types';

function starTwinkle(star: BgStar, base = 0.5, amplitude = 0.5, speedMul = 1): number {
    const p = getP5();
    return base + amplitude * p.sin(p.frameCount * star.twinkleSpeed * speedMul + star.twinkleOffset);
}

// ============================================
// drawStageEffects() - ステージエフェクトのメイン関数
// ============================================
export function drawStageEffects(stageCfg: StageConfig, alpha: number): void {
    if (alpha <= 0) {
        return;
    }
    // ステージ切替時に不正なマスクが残らないよう毎回リセット
    setLowOrbitPlanetMask(null);
    switch (stageCfg.specialEffect) {
        case 'lowOrbit': drawLowOrbitEffect(stageCfg, alpha); break;
        case 'starfield': drawStarfieldEffect(stageCfg, alpha); break;
        case 'asteroidBelt': drawAsteroidBeltEffect(stageCfg, alpha); break;
        case 'deepSpace': drawDeepSpaceEffect(stageCfg, alpha); break;
        case 'subspace': drawSubspaceEffect(stageCfg, alpha); break;
    }
}

// ============================================
// drawLowOrbitEffect() - 低軌道ステージエフェクト
// ============================================
export function drawLowOrbitEffect(_stageCfg: StageConfig, alpha: number): void {
    const p = getP5();
    const bgStars = getBgStars();
    const bgPlanetY = getBgPlanetY();

    // 地球の位置を計算（Three.jsと同じ座標系）
    const px = CONFIG.layout.gameAreaX + CONFIG.width - 20;
    const py = bgPlanetY - 150;
    const planetRadius = 275;  // EARTH_RADIUSと同じ

    // マスク情報を保存（流れる星の遮蔽用）
    setLowOrbitPlanetMask({
        x: px,
        y: py,
        r: planetRadius,
        r2: planetRadius * planetRadius
    });

    // 星を描画（地球に重なる星は非表示）
    const planetRadiusSq = planetRadius * planetRadius;
    for (const star of bgStars) {
        if (star.baseAlpha > 0.35) {
            continue;
        }
        const dx = star.x - px;
        const dy = star.y - py;
        if (dx * dx + dy * dy < planetRadiusSq) {
            continue;
        }

        const tw = starTwinkle(star, 0.6, 0.4, 1);
        p.fill(255, 255, 255, star.brightness * 0.35 * tw * alpha);
        p.noStroke();
        p.circle(star.x, star.y, star.size);
    }

    // 地球の位置更新（Three.jsで描画）
    setBgPlanetY(bgPlanetY + 0.4);
}

// ============================================
// drawStarfieldEffect() - 星空ステージエフェクト
// ============================================
export function drawStarfieldEffect(_stageCfg: StageConfig, alpha: number): void {
    const p = getP5();
    const bgStars = getBgStars();
    const bgBrightStars = getBgBrightStars();
    const dt = getDt();

    // 通常の星
    for (const star of bgStars) {
        const tw = starTwinkle(star, 0.5, 0.5, 1);
        p.fill(255, 255, 255, star.brightness * tw * alpha);
        p.noStroke();
        p.circle(star.x, star.y, star.size);
    }

    // 明るい星（手前に流れる）
    for (const star of bgBrightStars) {
        // 移動（dt適用）
        star.y += star.speed * dt;
        if (star.y > p.height + star.glowSize) {
            star.y = -star.glowSize;
            star.x = p.random(p.width);
        }

        const tw = p.max(0, starTwinkle(star, 0.5, 0.5, 1));
        // グロー（なめらかなグラデーション）
        for (let i = 5; i > 0; i--) {
            const glowAlpha = 8 * tw * alpha * (i / 5);
            p.fill(star.color[0], star.color[1], star.color[2], glowAlpha);
            p.noStroke();
            p.circle(star.x, star.y, star.glowSize * i * 0.4);
        }
        // 芯
        p.fill(star.color[0], star.color[1], star.color[2], star.brightness * tw * alpha);
        p.circle(star.x, star.y, star.size * (0.5 + tw * 0.5));
        // 十字きらめき（最大輝度時のみ）
        if (tw > 0.92) {
            p.stroke(255, 255, 255, 70 * (tw - 0.92) / 0.08 * alpha);
            p.strokeWeight(1);
            const sl = star.size * 2.5 * tw;
            p.line(star.x - sl, star.y, star.x + sl, star.y);
            p.line(star.x, star.y - sl, star.x, star.y + sl);
        }
    }
}

// ============================================
// drawAsteroidBeltEffect() - 小惑星帯エフェクト
// ============================================
export function drawAsteroidBeltEffect(_stageCfg: StageConfig, alpha: number): void {
    const p = getP5();

    drawDistantGiantAsteroid(alpha);

    const bgStars = getBgStars();
    const bgAsteroids = getBgAsteroids();

    // まず小惑星の位置を収集
    const asteroidPositions: Array<{x: number; y: number; size: number}> = [];
    for (const ast of bgAsteroids) {
        asteroidPositions.push({ x: ast.x, y: ast.y, size: ast.size * (0.5 + ast.layer * 0.25) });
    }

    // 星を描画（小惑星に重なる星は非表示）
    for (const star of bgStars) {
        if (star.baseAlpha > 0.7) {
            continue;
        }

        // 小惑星との重なりをチェック
        let hidden = false;
        for (const ast of asteroidPositions) {
            const dx = star.x - ast.x;
            const dy = star.y - ast.y;
            const distSq = dx * dx + dy * dy;
            const astRadiusSq = (ast.size * 1.2) * (ast.size * 1.2);
            if (distSq < astRadiusSq) {
                hidden = true;
                break;
            }
        }
        if (hidden) {
            continue;
        }

        p.fill(255, 255, 255, star.brightness * 0.5 * alpha);
        p.noStroke();
        p.circle(star.x, star.y, star.size * 0.8);
    }

    for (let layer = 0; layer < 3; layer++) {
        for (const ast of bgAsteroids) {
            if (ast.layer !== layer) {
                continue;
            }
            drawAsteroid(ast, alpha, layer);
        }
    }
    drawAsteroidDust(alpha);
}

// ============================================
// drawDistantGiantAsteroid() - 遠景の巨大小惑星
// ============================================
function drawDistantGiantAsteroid(alpha: number): void {
    const p = getP5();
    let distantGiantY = getDistantGiantY();

    distantGiantY += 0.12;
    if (distantGiantY > p.height + 100) {
        distantGiantY = -150;
    }
    setDistantGiantY(distantGiantY);

    const gx = p.width + 60, gy = distantGiantY, gr = 200;
    p.push();
    p.translate(gx, gy);
    p.rotate(p.frameCount * 0.0008);
    p.noStroke();

    // シェーダー風グラデーション（外側のぼかし）
    for (let i = 8; i > 0; i--) {
        const t = i / 8;
        p.fill(45 + t * 15, 35 + t * 10, 28 + t * 8, 4 * alpha * t);
        p.ellipse(0, 0, gr + i * 30, gr + i * 30);
    }

    // 本体グラデーション
    for (let r = gr; r > 0; r -= 12) {
        const t = r / gr;
        const baseR = 60 + (1 - t) * 35;
        const baseG = 45 + (1 - t) * 25;
        const baseB = 35 + (1 - t) * 20;
        p.fill(baseR, baseG, baseB, (80 + (1 - t) * 40) * alpha);
        p.ellipse(0, 0, r, r * 0.95);
    }

    // クレーター（グラデーション付き）
    for (let j = 3; j > 0; j--) {
        p.fill(50 - j * 5, 38 - j * 4, 30 - j * 3, (30 + j * 8) * alpha);
        p.ellipse(-30, -20, 50 * (j / 3), 40 * (j / 3));
        p.ellipse(20, 30, 40 * (j / 3), 30 * (j / 3));
    }

    // ハイライト
    for (let j = 3; j > 0; j--) {
        p.fill(100 + j * 10, 78 + j * 8, 60 + j * 6, (25 + j * 8) * alpha);
        p.ellipse(-45, -45, 65 * (j / 3), 50 * (j / 3));
    }
    p.pop();
}

// ============================================
// drawAsteroid() - 個別の小惑星を描画
// ============================================
function drawAsteroid(ast: BgAsteroid, alpha: number, layer: number): void {
    const p = getP5();

    ast.y += ast.speed * (0.5 + layer * 0.4);  // 速度を強匁E
    ast.x += (ast.driftX || 0) * (0.5 + layer * 0.3);  // 左右ドリフトを強匁E
    ast.rotation += ast.rotSpeed;

    // 画面外に出たらリセット
    if (ast.y > p.height + ast.size * 2) {
        ast.y = -ast.size * 3;
        ast.x = p.random(p.width * 0.1, p.width * 0.9);
    }
    if (ast.x < -ast.size * 3) {
        ast.x = p.width + ast.size * 2;
    }
    if (ast.x > p.width + ast.size * 3) {
        ast.x = -ast.size * 2;
    }

    const la = alpha * (0.35 + layer * 0.32);
    const ls = 0.5 + layer * 0.25;
    const cv = ast.colorVar || 0;

    p.push();
    p.translate(ast.x, ast.y);
    p.rotate(ast.rotation);
    p.scale(ls);

    // 形状計算関数
    function getRadius(a: number): number {
        return ast.size * (0.72 + p.sin(a * 2.7 + ast.shapeOffset) * ast.roughness + p.cos(a * 1.9 + ast.shapeOffset * 0.8) * ast.roughness * 0.6);
    }

    // シェーダー風：影のグラデーション
    for (let s = 4; s > 0; s--) {
        p.fill(20 + cv * 0.2, 18 + cv * 0.15, 15, (50 + s * 12) * la * (s / 4));
        p.noStroke();
        p.beginShape();
        for (let i = 0; i < ast.vertices; i++) {
            const a = (p.TWO_PI / ast.vertices) * i;
            const r = getRadius(a) * (1 + s * 0.03);
            p.vertex(p.cos(a) * r + 5 + s, p.sin(a) * r + 5 + s);
        }
        p.endShape(p.CLOSE);
    }

    // 本体：グラデーション効果（複数層）
    for (let s = 3; s > 0; s--) {
        const t = s / 3;
        p.fill(55 + cv + t * 20, 45 + cv * 0.8 + t * 18, 35 + cv * 0.5 + t * 15, (160 + s * 20) * la);
        p.noStroke();
        p.beginShape();
        for (let i = 0; i < ast.vertices; i++) {
            const a = (p.TWO_PI / ast.vertices) * i;
            const r = getRadius(a) * t;
            p.vertex(p.cos(a) * r, p.sin(a) * r);
        }
        p.endShape(p.CLOSE);
    }

    // エッジライン（微細）
    p.stroke(90 + cv, 78 + cv * 0.8, 62 + cv * 0.5, 40 * la);
    p.strokeWeight(0.8);
    p.noFill();
    p.beginShape();
    for (let i = 0; i < ast.vertices; i++) {
        const a = (p.TWO_PI / ast.vertices) * i;
        p.vertex(p.cos(a) * getRadius(a), p.sin(a) * getRadius(a));
    }
    p.endShape(p.CLOSE);

    // クレーター（グラデーション付き）
    p.noStroke();
    const craterPositions: Array<[number, number, number, number]> = [
        [-ast.size * 0.22, ast.size * 0.08, ast.size * 0.32, ast.size * 0.28],
        [ast.size * 0.12, -ast.size * 0.18, ast.size * 0.22, ast.size * 0.18]
    ];
    if (ast.craterCount > 2) {
        craterPositions.push([ast.size * 0.05, ast.size * 0.22, ast.size * 0.18, ast.size * 0.15]);
    }
    for (const crater of craterPositions) {
        for (let j = 3; j > 0; j--) {
            p.fill(42 + cv * 0.5 - j * 4, 35 + cv * 0.3 - j * 3, 28 - j * 2, (30 + j * 12) * la);
            p.ellipse(crater[0], crater[1], crater[2] * (j / 3), crater[3] * (j / 3));
        }
    }

    // ハイライト（グラデーション）
    for (let j = 4; j > 0; j--) {
        p.fill(95 + cv + j * 8, 82 + cv * 0.8 + j * 6, 68 + cv * 0.5 + j * 5, (25 + j * 10) * la * (j / 4));
        p.ellipse(-ast.size * 0.28, -ast.size * 0.26, ast.size * 0.4 * (j / 4), ast.size * 0.32 * (j / 4));
    }

    p.pop();
}

// ============================================
// drawAsteroidDust() - 小惑星の塵を描画
// ============================================
function drawAsteroidDust(alpha: number): void {
    const p = getP5();
    p.noStroke();
    for (let i = 0; i < 25; i++) {
        const dx = (p.frameCount * 0.25 + i * 47) % (p.width + 20) - 10;
        const dy = (p.frameCount * 0.6 + i * 73) % (p.height + 20) - 10;
        const ds = 1 + (i % 3);
        p.fill(100, 85, 70, (30 + (i % 4) * 10) * alpha);
        p.push();
        p.translate(dx, dy);
        p.rotate(i * 0.7 + p.frameCount * 0.008);
        p.beginShape();
        for (let j = 0; j < 4; j++) {
            const a = (p.TWO_PI / 4) * j;
            p.vertex(p.cos(a) * ds * (0.6 + (j % 2) * 0.6), p.sin(a) * ds * (0.6 + (j % 2) * 0.6));
        }
        p.endShape(p.CLOSE);
        p.pop();
    }
}

// ============================================
// drawDeepSpaceEffect() - 深宇宙エフェクト
// ============================================
export function drawDeepSpaceEffect(_stageCfg: StageConfig, alpha: number): void {
    const p = getP5();
    const bgStars = getBgStars();

    // 深宇宙の暗黒背景（星雲が映えるように暗く）
    p.fill(2, 1, 5, 30 * alpha);
    p.noStroke();
    p.rect(0, 0, p.width, p.height);

    // 星雲はThree.jsで描画されるため、p5.jsでは描画しない

    // 背景の固定星（非常に控えめ、星雲の引き立て役）
    for (const star of bgStars) {
        if (star.baseAlpha > 0.2) {
            continue;
        }  // さらに暗い星のみ

        // ゆっくりな瞬き
        const tw = starTwinkle(star, 0.8, 0.2, 0.2);

        // 非常に暗い星（星雲を邪魔しない）
        const twinkle = p.sin(p.frameCount * 0.03 + star.twinkleOffset) * 0.2 + 0.8;
        p.fill(180, 175, 200, star.brightness * 0.08 * tw * alpha * twinkle);
        p.circle(star.x, star.y, star.size * 0.4);
    }
}

// ============================================
// drawSubspaceEffect() - 亜空間エフェクト
// ============================================
export function drawSubspaceEffect(_stageCfg: StageConfig, alpha: number): void {
    const p = getP5();
    let subspaceWarpOffset = getSubspaceWarpOffset();

    subspaceWarpOffset += 0.028;
    setSubspaceWarpOffset(subspaceWarpOffset);

    // 3色のパルスが交互に現れる不気味な背景（さらに強化）
    const p1 = p.sin(subspaceWarpOffset * 0.7) * 0.5 + 0.5;  // 紫
    const p2 = p.sin(subspaceWarpOffset * 0.9 + 2.1) * 0.5 + 0.5;  // 緁E
    const p3 = p.sin(subspaceWarpOffset * 1.1 + 4.2) * 0.5 + 0.5;  // 赤

    p.noStroke();
    // 基本の色パルス（さらに強化）
    p.fill(80, 22, 110, 22 * p1 * alpha);
    p.rect(0, 0, p.width, p.height);
    p.fill(30, 95, 55, 18 * p2 * alpha);
    p.rect(0, 0, p.width, p.height);
    p.fill(115, 22, 35, 16 * p3 * alpha);
    p.rect(0, 0, p.width, p.height);

    // 不気味な霧（グラデーション）
    drawSubspaceMist(alpha);

    // 追加：うねる波紋の外縁
    drawSubspaceEdgeGlow(alpha);

    drawSubspaceRipples(alpha);
    drawSubspaceFissures(alpha);
    drawSubspaceGlimmers(alpha);
    drawSubspaceVoid(alpha);
    drawSubspaceStreaks(alpha);
}

// ============================================
// drawSubspaceEdgeGlow() - 画面端のうねる光
// ============================================
function drawSubspaceEdgeGlow(alpha: number): void {
    const p = getP5();
    const subspaceWarpOffset = getSubspaceWarpOffset();
    p.noStroke();

    // 左端
    for (let i = 0; i < 6; i++) {
        const edgeY = p.height * 0.3 + p.sin(subspaceWarpOffset * 0.3 + i * 0.8) * p.height * 0.25;
        const edgeW = 30 + p.sin(subspaceWarpOffset * 0.5 + i) * 15;
        const colorT = (subspaceWarpOffset * 0.15 + i * 0.5) % 3;
        let er, eg, eb;
        if (colorT < 1) {
            er = 90; eg = 30; eb = 120;
        } else if (colorT < 2) {
            er = 40; eg = 100; eb = 60;
        } else {
            er = 120; eg = 35; eb = 50;
        }
        p.fill(er, eg, eb, 12 * alpha);
        p.ellipse(-5, edgeY, edgeW, 80 + i * 20);
    }

    // 右端
    for (let i = 0; i < 6; i++) {
        const edgeY = p.height * 0.5 + p.cos(subspaceWarpOffset * 0.35 + i * 0.7) * p.height * 0.3;
        const edgeW = 35 + p.cos(subspaceWarpOffset * 0.45 + i) * 18;
        const colorT = (subspaceWarpOffset * 0.12 + i * 0.6 + 1.5) % 3;
        let er, eg, eb;
        if (colorT < 1) {
            er = 100; eg = 35; eb = 130;
        } else if (colorT < 2) {
            er = 45; eg = 110; eb = 70;
        } else {
            er = 130; eg = 40; eb = 55;
        }
        p.fill(er, eg, eb, 10 * alpha);
        p.ellipse(p.width + 5, edgeY, edgeW, 90 + i * 22);
    }
}

// ============================================
// drawSubspaceStreaks() - 光の筋（強化）
// ============================================
function drawSubspaceStreaks(alpha: number): void {
    const p = getP5();
    const subspaceWarpOffset = getSubspaceWarpOffset();

    // 不規則に走る光の筋（数を増加）
    for (let i = 0; i < 8; i++) {
        const streakPhase = subspaceWarpOffset * 0.35 + i * 1.2;
        const streakAlpha = p.sin(streakPhase) * 0.5 + 0.5;
        if (streakAlpha > 0.5) {
            const sx = p.sin(i * 2.5 + subspaceWarpOffset * 0.12) * p.width * 0.45 + p.width * 0.5;
            const sy = p.cos(i * 1.8 + subspaceWarpOffset * 0.09) * p.height * 0.35 + p.height * 0.4;
            const angle = p.sin(subspaceWarpOffset * 0.22 + i) * 0.6;
            const len = 50 + p.sin(streakPhase * 2) * 30;

            // 色を3色から選択（より鮮やか）
            const colorChoice = (i + p.floor(subspaceWarpOffset * 0.5)) % 3;
            if (colorChoice === 0) {
                p.stroke(160, 70, 200, 40 * streakAlpha * alpha);
            } else if (colorChoice === 1) {
                p.stroke(70, 180, 100, 35 * streakAlpha * alpha);
            } else {
                p.stroke(200, 70, 90, 35 * streakAlpha * alpha);
            }
            p.strokeWeight(2.5);
            p.line(sx, sy, sx + p.cos(angle) * len, sy + p.sin(angle) * len);
        }
    }
}

// ============================================
// drawSubspaceMist() - 不気味な霧エフェクト
// ============================================
function drawSubspaceMist(alpha: number): void {
    const p = getP5();
    const subspaceWarpOffset = getSubspaceWarpOffset();
    p.noStroke();

    // 画面に漂う薄い霧（3色が混ざる）- 6つに増加
    for (let i = 0; i < 6; i++) {
        const mx = p.width * 0.2 + p.sin(subspaceWarpOffset * 0.09 + i * 1.3) * p.width * 0.6;
        const my = p.height * 0.15 + p.cos(subspaceWarpOffset * 0.07 + i * 1.6) * p.height * 0.45;
        const mSize = 230 + p.sin(subspaceWarpOffset * 0.22 + i) * 60;
        const mistPulse = p.sin(subspaceWarpOffset * 0.45 + i * 0.65) * 0.4 + 0.6;

        // 色相が徐々に変化（より鮮やか）
        const colorT = (subspaceWarpOffset * 0.12 + i * 0.65) % 3;
        let mr, mg, mb;
        if (colorT < 1) {
            mr = p.lerp(75, 35, colorT); mg = p.lerp(22, 75, colorT); mb = p.lerp(100, 60, colorT);
        } else if (colorT < 2) {
            mr = p.lerp(35, 100, colorT - 1); mg = p.lerp(75, 30, colorT - 1); mb = p.lerp(60, 42, colorT - 1);
        } else {
            mr = p.lerp(100, 75, colorT - 2); mg = p.lerp(30, 22, colorT - 2); mb = p.lerp(42, 100, colorT - 2);
        }

        // グラデーション霧（より多層に）
        for (let j = 8; j > 0; j--) {
            p.fill(mr, mg, mb, 6 * mistPulse * alpha * (j / 8));
            p.ellipse(mx, my, mSize * (j / 8), mSize * 0.58 * (j / 8));
        }
    }
}

// ============================================
// drawSubspaceVoid() - 不気味な虚無の穴
// ============================================
function drawSubspaceVoid(alpha: number): void {
    const p = getP5();
    const subspaceWarpOffset = getSubspaceWarpOffset();

    // 不気味な虚無の穴（シェーダー風グラデーション）
    const voidPulse = p.sin(subspaceWarpOffset * 0.5) * 0.3 + 0.7;
    const vx = p.width * 0.5 + p.sin(subspaceWarpOffset * 0.15) * 80;
    const vy = p.height * 0.35 + p.cos(subspaceWarpOffset * 0.12) * 60;

    p.noStroke();
    // 外縁のグラデーション（緑紫赤が混ざる）
    for (let i = 10; i > 0; i--) {
        const t = i / 10;
        const phase = subspaceWarpOffset * 0.6 + i * 0.25;
        const glowR = 35 + 55 * p.sin(phase);
        const glowG = 60 + 45 * p.sin(phase + 2.1);
        const glowB = 50 + 40 * p.cos(phase + 1);
        p.fill(glowR, glowG, glowB, (10 - i * 0.8) * voidPulse * alpha * t);
        p.ellipse(vx, vy, 70 + i * 22, 40 + i * 13);
    }
    // 中心の暗黒（グラデーション）
    for (let i = 4; i > 0; i--) {
        p.fill(8 - i, 4 - i * 0.5, 15 - i, (35 + i * 8) * alpha * (i / 4));
        p.ellipse(vx, vy, 60 * (i / 4), 35 * (i / 4));
    }
}

// ============================================
// drawSubspaceRipples() - うねる波紋
// ============================================
function drawSubspaceRipples(alpha: number): void {
    const p = getP5();
    const subspaceWarpOffset = getSubspaceWarpOffset();
    p.noFill();

    for (let layer = 0; layer < 5; layer++) {
        const lp = subspaceWarpOffset + layer * 0.5;
        const pulse = p.sin(lp) * 0.5 + 0.5;

        // レイヤーごとに色相を変える（紫→緑→赤の循環）
        const colorPhase = (layer + subspaceWarpOffset * 0.3) % 3;
        let r, g, b;
        if (colorPhase < 1) {
            // 紫
            r = 80 + p.sin(lp * 1.1) * 30;
            g = 30 + p.sin(lp * 0.6 + 1) * 20;
            b = 100 + p.cos(lp * 0.9) * 40;
        } else if (colorPhase < 2) {
            // 緑
            r = 30 + p.sin(lp * 0.8) * 20;
            g = 90 + p.sin(lp * 1.2 + 1) * 40;
            b = 50 + p.cos(lp * 0.7) * 30;
        } else {
            // 赤
            r = 100 + p.sin(lp) * 40;
            g = 25 + p.sin(lp * 0.5 + 1) * 15;
            b = 40 + p.cos(lp * 0.8) * 25;
        }

        p.stroke(r, g, b, (18 + pulse * 12) * alpha);
        p.strokeWeight(1.5 + layer * 0.25);
        const cx = p.width * 0.5 + p.sin(lp * 0.25) * 45;
        const cy = p.height * 0.4 + p.cos(lp * 0.3) * 35;
        const bs = 50 + layer * 80;
        const dist = p.sin(lp * 1.8) * 30;
        p.beginShape();
        for (let a = 0; a < p.TWO_PI; a += 0.07) {
            const w = p.sin(a * 4 + lp * 2.5) * dist;
            const rx = (bs + w) * (1 + p.sin(a * 2.5) * 0.2);
            const ry = (bs * 0.5 + w * 0.5) * (1 + p.cos(a * 2) * 0.15);
            p.vertex(cx + p.cos(a) * rx, cy + p.sin(a) * ry);
        }
        p.endShape(p.CLOSE);
    }
}

// ============================================
// drawSubspaceFissures() - 裂け目
// ============================================
function drawSubspaceFissures(alpha: number): void {
    const p = getP5();
    const subspaceWarpOffset = getSubspaceWarpOffset();

    // 紫の裂け目
    p.stroke(120, 50, 150, 25 * alpha);
    p.strokeWeight(2.5);
    for (let i = 0; i < 2; i++) {
        const sx = p.width * 0.2 + p.sin(subspaceWarpOffset * 0.35 + i * 2.5) * p.width * 0.35;
        const sy = p.height * 0.12 + i * p.height * 0.35;
        p.beginShape();
        for (let t = 0; t <= 1; t += 0.06) {
            p.vertex(
                p.lerp(sx, sx + p.sin(subspaceWarpOffset * 0.8 + i) * 70, t) + p.sin(t * 7 + subspaceWarpOffset * 3.5) * 15,
                p.lerp(sy, sy + p.cos(subspaceWarpOffset * 0.6 + i) * 100, t) + p.cos(t * 5 + subspaceWarpOffset * 2.5) * 12
            );
        }
        p.endShape();
    }

    // 緑の裂け目
    p.stroke(40, 130, 70, 20 * alpha);
    p.strokeWeight(2);
    for (let i = 0; i < 2; i++) {
        const sx = p.width * 0.7 + p.cos(subspaceWarpOffset * 0.4 + i * 2.8) * p.width * 0.2;
        const sy = p.height * 0.2 + i * p.height * 0.38;
        p.beginShape();
        for (let t = 0; t <= 1; t += 0.08) {
            p.vertex(
                sx + p.sin(t * 6 + subspaceWarpOffset * 3.2 + i) * 18,
                sy + t * 90 + p.cos(t * 5 + subspaceWarpOffset * 2.8) * 14
            );
        }
        p.endShape();
    }

    // 赤の裂け目
    p.stroke(150, 40, 60, 18 * alpha);
    p.strokeWeight(1.5);
    for (let i = 0; i < 2; i++) {
        const sx = p.width * 0.45 + p.sin(subspaceWarpOffset * 0.5 + i * 3.2) * p.width * 0.25;
        const sy = p.height * 0.5 + i * p.height * 0.2;
        p.beginShape();
        for (let t = 0; t <= 1; t += 0.1) {
            p.vertex(
                sx + p.sin(t * 9 + subspaceWarpOffset * 4.5 + i) * 12,
                sy + t * 70 + p.cos(t * 7 + subspaceWarpOffset * 3.5) * 10
            );
        }
        p.endShape();
    }
}

// ============================================
// drawSubspaceGlimmers() - 不気味に明滅する光点
// ============================================
function drawSubspaceGlimmers(alpha: number): void {
    const p = getP5();

    // 不気味に明滅する光点（緑、紫、赤がランダムに）
    for (let i = 0; i < 15; i++) {
        const fl = p.sin(p.frameCount * 0.08 + i * 2.3) * 0.5 + 0.5;
        if (fl > 0.55) {
            const px = (i * 53 + p.frameCount * 0.12) % p.width;
            const py = (i * 89 + p.sin(p.frameCount * 0.03 + i) * 70) % p.height;
            const intensity = (fl - 0.55) / 0.45;
            const cc = (i + p.floor(p.frameCount * 0.02)) % 3;

            // グロー
            if (cc === 0) {
                p.fill(100, 50, 150, 25 * intensity * alpha);  // 紫
                p.circle(px, py, 8 + intensity * 6);
                p.fill(140, 70, 180, 45 * intensity * alpha);
            } else if (cc === 1) {
                p.fill(40, 120, 60, 22 * intensity * alpha);  // 緑
                p.circle(px, py, 8 + intensity * 6);
                p.fill(60, 160, 90, 40 * intensity * alpha);
            } else {
                p.fill(140, 40, 50, 20 * intensity * alpha);  // 赤
                p.circle(px, py, 8 + intensity * 6);
                p.fill(180, 60, 70, 38 * intensity * alpha);
            }
            p.noStroke();
            p.circle(px, py, 2 + intensity * 4);
        }
    }
}
